<script setup lang="ts">
/**
 * What the page a break opens looks like.
 *
 * A break is mostly a break. What it has to set is the page it starts, since
 * a page is not a thing this document holds until it is printed and the break
 * is where an author says one begins: whether that page is a double page, and
 * the picture behind it.
 *
 * The double page comes first because it decides what the picture covers: a
 * picture chosen for a single page and then stretched across two is a picture
 * chosen twice.
 *
 * The fit and the strength only appear once there is a picture, because until
 * then they are two questions about nothing.
 */
import { useI18n } from 'vue-i18n'

import { computed } from 'vue'

import {
  BACKGROUND_FIT_IDS,
  BACKGROUND_STRENGTH_IDS,
  hasBackground,
  type BackgroundFit,
  type BackgroundStrength,
  type PageBackground,
} from '@/domain/layout/background'
import type { PageBreakSection } from '@/domain/section'
import ImagePicker from '@/sections/ImagePicker.vue'
import InspectorField from '@/sections/InspectorField.vue'

const { t } = useI18n()

const props = defineProps<{ section: PageBreakSection }>()
const emit = defineEmits<{ change: [section: PageBreakSection] }>()

const painted = computed(() => hasBackground(props.section.background))

function setBackground(changes: Partial<PageBackground>): void {
  emit('change', {
    ...props.section,
    background: { ...props.section.background, ...changes },
  })
}

function setSpread(spread: boolean): void {
  emit('change', { ...props.section, spread })
}
</script>

<template>
  <div class="fields">
    <p class="note">{{ t('inspector.pageBreak.note') }}</p>

    <label class="b-switch spread">
      <input
        type="checkbox"
        :checked="section.spread"
        @change="setSpread(($event.target as HTMLInputElement).checked)"
      />
      <span class="b-switch__track" />
      <span class="spread__text">
        {{ t('inspector.pageBreak.spread') }}
        <span class="spread__hint">{{ t('inspector.pageBreak.spreadHint') }}</span>
      </span>
    </label>

    <ImagePicker
      :source="section.background.image"
      :label="t('inspector.pageBreak.background')"
      :hint="t('inspector.pageBreak.backgroundHint')"
      @change="setBackground({ image: $event })"
    />

    <!-- Two questions about nothing, until there is a picture. -->
    <template v-if="painted">
      <InspectorField
        :label="t('inspector.pageBreak.fit')"
        :hint="t('inspector.pageBreak.fitHint')"
      >
        <div class="segments" role="radiogroup" :aria-label="t('inspector.pageBreak.fit')">
          <button
            v-for="option in BACKGROUND_FIT_IDS"
            :key="option"
            type="button"
            role="radio"
            class="segment"
            :class="{ 'segment--on': option === section.background.fit }"
            :aria-checked="option === section.background.fit"
            @click="setBackground({ fit: option as BackgroundFit })"
          >
            {{ t(`inspector.pageBreak.fits.${option}`) }}
          </button>
        </div>
      </InspectorField>

      <InspectorField
        :label="t('inspector.pageBreak.strength')"
        :hint="t('inspector.pageBreak.strengthHint')"
      >
        <div class="segments" role="radiogroup" :aria-label="t('inspector.pageBreak.strength')">
          <button
            v-for="option in BACKGROUND_STRENGTH_IDS"
            :key="option"
            type="button"
            role="radio"
            class="segment"
            :class="{ 'segment--on': option === section.background.strength }"
            :aria-checked="option === section.background.strength"
            @click="setBackground({ strength: option as BackgroundStrength })"
          >
            {{ t(`inspector.pageBreak.strengths.${option}`) }}
          </button>
        </div>
      </InspectorField>

      <p class="note">{{ t('inspector.pageBreak.bleedNote') }}</p>
    </template>
  </div>
</template>

<style scoped>
.fields {
  display: flex;
  flex-direction: column;
  gap: var(--sp-6);
}

.spread {
  align-items: flex-start;
}

.spread__text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: var(--text-base);
}

.spread__hint {
  color: var(--ink-faint);
  font-size: var(--text-xs);
  line-height: 1.5;
}

.note {
  margin: 0;
  color: var(--ink-faint);
  font-size: var(--text-xs);
  line-height: 1.5;
}

.segments {
  display: flex;
  flex-wrap: wrap;
  gap: 1px;
  padding: 2px;
  border-radius: var(--radius);
  background: var(--panel-raised);
  box-shadow: inset 0 0 0 1px var(--hairline-strong);
}

.segment {
  flex: 1;
  min-width: 0;
  height: 26px;
  padding: 0 var(--sp-3);
  border: 0;
  border-radius: var(--radius-xs);
  background: none;
  color: var(--ink-soft);
  font: inherit;
  font-size: var(--text-xs);
  white-space: nowrap;
  cursor: pointer;
  transition:
    background var(--quick),
    color var(--quick);
}

.segment:hover {
  color: var(--ink);
}

.segment--on {
  background: var(--accent-quiet);
  color: var(--accent);
  font-weight: 600;
}
</style>
